import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import { AuthModel, SecondFactorMethod, TOTP_CODE_LENGTH } from '../core/_models'
import { getPasskeyLoginOptions, submitSecondFactor } from '../core/_requests'
import { credentialToJson, isWebAuthnAvailable, toRequestOptions } from '../core/webauthn'
import { getErrorCode } from '../../../common/api/errors'

type Props = {
  challenge: string
  methods: SecondFactorMethod[]
  /** May return a promise: the step stays busy until the sign-in it handed over has finished. */
  onSuccess: (auth: AuthModel) => void | Promise<void>
  /** The confirmation can no longer be finished: back to the password, with the reason. */
  onRestart: (message: string) => void
}

type View = 'passkey' | 'totp' | 'recovery_code'

const DIGITS_ONLY = /^\d*$/

const TEXT = {
  wrongCode: 'Неверный код.',
  tooMany: 'Превышено допустимое количество попыток. Пожалуйста, попробуйте снова позже.',
  expired: 'Время на подтверждение истекло. Войдите заново.',
  failed: 'Не удалось подтвердить вход. Попробуйте ещё раз.',
  keyFailed: 'Ключ не сработал. Введите код из приложения.',
}

/**
 * The second step of a sign-in. With a passkey enrolled (and a browser able to use one) it opens on
 * the key; the authenticator code is always the other way, and a recovery code the last one.
 */
export const SecondFactorStep: FC<Props> = ({ challenge, methods, onSuccess, onRestart }) => {
  const canUseKey = methods.includes('passkey') && isWebAuthnAvailable()
  const [view, setView] = useState<View>(canUseKey ? 'passkey' : 'totp')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const codeInput = useRef<HTMLInputElement>(null)

  // the field is disabled while the request runs and loses the cursor: give it back for the retry
  useEffect(() => {
    if (!loading && message) {
      codeInput.current?.focus()
    }
  }, [loading, message])

  const show = (next: View) => {
    setView(next)
    setCode('')
    setMessage(null)
  }

  /** @returns whether the failure was dealt with here (true) or the step is over (restart) */
  const explain = (error: unknown, wrongText: string) => {
    const status = (error as { status?: number } | undefined)?.status
    const errorCode = getErrorCode(error)

    if (errorCode === 'SECOND_FACTOR_CHALLENGE_EXPIRED') {
      onRestart(TEXT.expired)
    } else if (status === 429) {
      setMessage(TEXT.tooMany)
    } else if (errorCode === 'SECOND_FACTOR_INVALID') {
      setMessage(wrongText)
    } else {
      setMessage(TEXT.failed)
    }
  }

  const submitCode = async (event: FormEvent) => {
    event.preventDefault()
    if (loading) {
      return
    }

    setLoading(true)
    setMessage(null)
    try {
      const { data } = await submitSecondFactor(challenge, view as 'totp' | 'recovery_code', { code: code.trim() })
      await onSuccess(data)
    } catch (error) {
      setCode('')
      explain(error, TEXT.wrongCode)
    } finally {
      setLoading(false)
    }
  }

  const useKey = async () => {
    if (loading) {
      return
    }

    setLoading(true)
    setMessage(null)
    try {
      const { data: prepared } = await getPasskeyLoginOptions(challenge)
      const credential = (await navigator.credentials.get({
        publicKey: toRequestOptions(prepared.options),
      })) as PublicKeyCredential | null
      if (!credential) {
        throw new Error('no credential')
      }

      const { data } = await submitSecondFactor(challenge, 'passkey', { assertion: credentialToJson(credential) })
      await onSuccess(data)
    } catch (error) {
      if (getErrorCode(error) === 'SECOND_FACTOR_CHALLENGE_EXPIRED') {
        onRestart(TEXT.expired)
      } else {
        // a cancelled prompt, a missing key, a refusal: the code is still there
        setView('totp')
        setCode('')
        setMessage((error as { status?: number } | undefined)?.status === 429 ? TEXT.tooMany : TEXT.keyFailed)
      }
    } finally {
      setLoading(false)
    }
  }

  const isTotp = view === 'totp'
  const codeReady = isTotp ? code.length === TOTP_CODE_LENGTH : code.trim().length > 0

  return (
    <div className='w-100' id='kt_login_second_factor'>
      <div className='text-center mb-10'>
        <h1 className='text-dark mb-3'>Подтверждение входа</h1>
        <div className='text-gray-600 fw-semibold fs-6'>
          Учётная запись защищена вторым фактором.
        </div>
      </div>

      {message && (
        <div className='mb-8 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{message}</div>
        </div>
      )}

      {view === 'passkey' ? (
        <>
          <div className='d-grid mb-6'>
            <button type='button' className='btn btn-primary' onClick={useKey} disabled={loading}>
              {loading ? (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Ожидаем ключ...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              ) : (
                'Войти с ключом'
              )}
            </button>
          </div>
          <div className='text-center'>
            <button type='button' className='btn btn-link p-0' onClick={() => show('totp')} disabled={loading}>
              Ввести код из приложения
            </button>
          </div>
        </>
      ) : (
        <form className='form w-100' onSubmit={submitCode} noValidate>
          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark' htmlFor='second_factor_code'>
              {isTotp ? 'Код из приложения' : 'Резервный код'}
            </label>
            <input
              id='second_factor_code'
              ref={codeInput}
              type='text'
              inputMode={isTotp ? 'numeric' : 'text'}
              autoComplete={isTotp ? 'one-time-code' : 'off'}
              autoFocus
              className='form-control bg-transparent'
              value={code}
              disabled={loading}
              onChange={(event) => {
                const value = event.target.value
                if (!isTotp || (DIGITS_ONLY.test(value) && value.length <= TOTP_CODE_LENGTH)) {
                  setCode(value)
                }
              }}
            />
          </div>

          <div className='d-grid mb-6'>
            <button type='submit' className='btn btn-primary' disabled={loading || !codeReady}>
              {loading ? (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Проверяем...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              ) : (
                'Подтвердить'
              )}
            </button>
          </div>

          <div className='d-flex flex-column align-items-center gap-2'>
            {canUseKey && (
              <button type='button' className='btn btn-link p-0' onClick={() => show('passkey')} disabled={loading}>
                Войти с ключом
              </button>
            )}
            <button
              type='button'
              className='btn btn-link p-0'
              onClick={() => show(isTotp ? 'recovery_code' : 'totp')}
              disabled={loading}
            >
              {isTotp ? 'Использовать резервный код' : 'Ввести код из приложения'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
